import { useState, useMemo, useEffect, useRef } from 'react';
import { useUiFeedbackContext } from '@/contexts/UiFeedbackContext';
import { formatDate as formatDateShared, formatTime as formatTimeShared, localDayKey } from '@/utils/dates';
import { VIBE_EMOJIS, vibeDisplay } from '@/utils/vibes';
import { ArrowLeft, MapPin, Loader2, Trash2, ChevronRight, ChevronDown, Clock, Star, Check, Map, Copy, Calendar, MapPinned, Pencil, X as XIcon, ChevronUp, Merge, ThumbsUp, ThumbsDown, Share2, Lock, Crown, BookmarkMinus, Unlink2, Pin, PinOff, Sparkles } from 'lucide-react';
import { MAX_FREE_DAY_GROUPS, MAX_FREE_LOCATION_GROUPS, isTripArchivedHybrid, isGroupArchived, daysUntilArchive, generateTitle, saveReevaluatedTrip, type SavedItinerary, type SavedPlace, type GroupedItineraries } from '@/hooks/useItineraries';
import { ReevaluatePanel } from '@/components/ReevaluatePanel';
import type { ReevaluatedPlace, ReevaluateContext } from '@/services/reevaluate';
import type { Language } from '@/translations';
import type { RankedPlace } from '@/hooks/useLLM';
import type { TripGrouping } from '@/hooks/useSettings';
import { useLanguage } from '@/hooks/useLanguage';
import { formatRating } from '@/utils/formatters';
import { placePhotoUrl, staticMapUrl } from '@/utils/googleProxy';
import { useAuth } from '@/hooks/useAuth';
import { getLocalSignal, setTasteSignal, recordTasteEvent } from '@/lib/tasteGraph';

interface SavedTripsScreenProps {
  groupedItineraries: GroupedItineraries[];
  onBackToPlanning: () => void;
  onDeleteItinerary: (id: string) => Promise<void>;
  onDeleteGroup: (ids: string[]) => Promise<void>;
  onDeletePlace: (placeId: string, itineraryId: string) => Promise<void>;
  onFindMorePlaces: (itinerary: SavedItinerary) => Promise<RankedPlace[]>;
  onAddPlacesToTrip: (itineraryId: string, places: RankedPlace[]) => Promise<void>;
  onRenameTrip: (id: string, newTitle: string) => Promise<boolean>;
  onUnsaveNewPlace?: (itineraryId: string, placeId: string) => Promise<void>;
  onShareTrip?: (itineraryId: string) => Promise<string | null>;
  onUnshareTrip?: (itineraryId: string) => Promise<boolean>;
  loading: boolean;
  loadingMore: boolean;
  groupingType: TripGrouping;
  onGroupingChange: (grouping: TripGrouping) => void;
  isPremium: boolean;
  selectNewestOnOpen?: boolean;
  resetToSummary?: number;
  onHome?: () => void;
  onUpgrade?: () => void;
  onMergeTrips?: (targetId: string, sourceIds: string[], mergedMetadata: {
    location: string;
    time_available: string;
    transport: string;
    vibes: string[];
    ai_mode: string;
    sourceLocations?: string[];
    renamedTitles?: { title: string; placeCount: number }[];
    pinnedTitle?: string | null;
    pinned?: boolean;
  }, dedupedPlaces: SavedPlace[]) => Promise<void>;
  /** H-213 v2: pin / unpin. Up to 2 pinned trips per user (FIFO on slot 2). */
  onSetPinned?: (id: string) => Promise<boolean>;
  onUnpin?: (id: string) => Promise<boolean>;
  /** H-214: parent reloads its trips list after a reevaluation fork lands. */
  onReevaluationSaved?: () => Promise<void> | void;
}

const TRANSPORT_ICONS: Record<string, string> = {
  walking: '🚶',
  transit: '🚇',
  taxi: '🚕'
};

function SavedPOICard({
  place,
  transport,
  onDelete
}: {
  place: SavedPlace;
  transport: string;
  onDelete: () => void;
}) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [signal, setSignal] = useState<'like' | 'dislike' | null>(() => getLocalSignal(place.place_id));
  const [signalToast, setSignalToast] = useState<string | null>(null);
  const signalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (signalTimerRef.current) clearTimeout(signalTimerRef.current); }, []);

  const handleSignal = (e: React.MouseEvent, type: 'like' | 'dislike') => {
    e.stopPropagation();
    const userId = user?.id ?? null;
    const newSignal = signal === type ? null : type;
    setSignal(newSignal);
    setTasteSignal(userId, place.place_id, newSignal);

    if (signalTimerRef.current) clearTimeout(signalTimerRef.current);

    if (newSignal !== null) {
      recordTasteEvent(userId, place.place_id, type, {
        name: place.name, types: place.types ?? [], category: place.category ?? undefined, matchingVibes: place.matching_vibes ?? [],
      });
      setSignalToast(type === 'like' ? `${t.poi.moreLikeThisConfirm} ✓` : `${t.poi.lessLikeThisConfirm} ✓`);
    } else {
      recordTasteEvent(userId, place.place_id, type === 'like' ? 'unlike' : 'undislike', {
        name: place.name, types: place.types ?? [],
      });
      setSignalToast(t.poi.cleared);
    }
    signalTimerRef.current = setTimeout(() => setSignalToast(null), 2000);
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    const travelMode = transport === 'walking' ? 'walking' : transport === 'transit' ? 'transit' : 'driving';
    const destination = (place.location_lat && place.location_lng)
      ? `${place.location_lat},${place.location_lng}`
      : encodeURIComponent(place.formatted_address || place.address || place.name);
    const placeIdParam = place.place_id ? `&destination_place_id=${encodeURIComponent(place.place_id)}` : '';
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}${placeIdParam}&travelmode=${travelMode}`, '_blank');
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = `${place.name}\n${place.formatted_address || place.address || ''}`;
    navigator.clipboard.writeText(text);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmDelete) {
      onDelete();
    } else {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 5000);
    }
  };

  // weekdayDescriptions is Mon-Sun (0-6), JS getDay() is Sun(0)-Sat(6)
  const getDestinationDay = () => {
    const day = new Date().getDay();
    return day === 0 ? 6 : day - 1;
  };

  const openStatus = place.opening_hours ? {
    isOpen: place.opening_hours.openNow,
    status: place.opening_hours.openNow ? t.poi.open : t.poi.closed
  } : null;

  const categoryEmoji = place.category?.toLowerCase().includes('food') || place.category?.toLowerCase().includes('restaurant') ? '🍽️'
    : place.category?.toLowerCase().includes('bar') ? '🍺'
    : place.category?.toLowerCase().includes('museum') ? '🏛️'
    : place.category?.toLowerCase().includes('park') ? '🌳'
    : '📍';

  const hasPhoto = !!place.photo_url;

  return (
    <>
      <div
        className="glass-morphism rounded-2xl overflow-hidden card-hover cursor-pointer"
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${place.name}, ${isExpanded ? 'collapse' : 'expand'} details`}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsExpanded(!isExpanded); } }}
      >
        <div
          className="relative w-full"
          style={{ height: hasPhoto ? '160px' : '80px' }}
        >
          {hasPhoto ? (
            <>
              <img
                src={place.photo_url!}
                alt={place.name}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  (e.currentTarget.nextElementSibling as HTMLElement)?.style.setProperty('display', 'flex');
                }}
              />
              <div className="w-full h-full bg-[var(--bg-elevated)] items-center justify-center text-4xl" style={{ display: 'none' }}>
                {categoryEmoji}
              </div>
            </>
          ) : (
            <div className="w-full h-full bg-[var(--bg-elevated)] flex items-center justify-center text-4xl">
              {categoryEmoji}
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          <div className="absolute bottom-0 left-0 right-0 p-3">
            <div className="flex items-end justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-white leading-tight drop-shadow">
                  {place.name}
                </h3>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  {place.category && (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-[var(--accent-teal)]/80 text-white text-xs font-medium backdrop-blur-sm">
                      {place.category}
                    </span>
                  )}
                  {place.matching_vibes && place.matching_vibes.length > 0 && (
                    <>
                      {place.matching_vibes.map(vibe => {
                        const isCustom = vibe.startsWith('custom:');
                        const display = vibeDisplay(t, vibe);
                        if (!display) return null;
                        return isCustom ? (
                          <span
                            key={vibe}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/70 text-white text-xs font-medium backdrop-blur-sm"
                            title={display.label}
                          >
                            <span>{display.emoji}</span>
                            <span>{display.label}</span>
                          </span>
                        ) : (
                          <span key={vibe} className="text-sm drop-shadow" title={display.label}>
                            {display.emoji}
                          </span>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {place.rating && (
                  <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2 py-0.5">
                    <Star className="w-3.5 h-3.5 text-[var(--accent-gold)] fill-current" />
                    <span className="text-sm text-white font-medium">{formatRating(place.rating)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="absolute top-2 right-2">
            <div className="w-6 h-6 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
              {isExpanded
                ? <ChevronUp className="w-3.5 h-3.5 text-white" />
                : <ChevronDown className="w-3.5 h-3.5 text-white" />
              }
            </div>
          </div>

          <div className="absolute top-2 left-2">
            <button
              onClick={(e) => { e.stopPropagation(); handleDeleteClick(e); }}
              className={`h-9 rounded-lg border-2 flex items-center justify-center gap-1.5 px-2 flex-shrink-0 transition-all focus:outline-none backdrop-blur-sm ${
                confirmDelete
                  ? 'bg-[var(--accent-teal)] border-[var(--accent-teal)] text-white'
                  : 'border-white/70 bg-black/30 text-white/90 hover:bg-[var(--accent-teal)]/20 hover:border-[var(--accent-teal)] hover:text-[var(--accent-teal)]'
              }`}
              aria-label={confirmDelete ? 'Confirm remove from trip' : `Remove ${place.name} from trip`}
            >
              <BookmarkMinus className="w-4 h-4" />
              {confirmDelete && <span className="text-xs font-medium">Remove?</span>}
            </button>
          </div>
        </div>

        <div className="p-3 space-y-2">
          {place.hook && (
            <p className={`text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-line ${isExpanded ? '' : 'line-clamp-2'}`}>
              {place.hook}
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap text-xs text-[var(--text-secondary)]">
            {place.estimated_duration && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {place.estimated_duration}
              </span>
            )}
            {place.distance_note && <span>{place.distance_note}</span>}
            {openStatus && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${openStatus.isOpen ? 'bg-[var(--status-green-bg)] text-[var(--status-green)]' : 'bg-[var(--status-amber-bg)] text-[var(--status-amber)]'}`}>
                {openStatus.status}
              </span>
            )}
          </div>

          {!isExpanded && (
            <div className="flex gap-2 pt-1 items-center">
              <button
                onClick={handleNavigate}
                aria-label={`Navigate to ${place.name}`}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-teal)] transition-colors flex items-center justify-center gap-1"
              >
                <Map className="w-4 h-4" />
                {t.common.navigate}
              </button>
              <button
                onClick={handleCopy}
                aria-label={`Copy address of ${place.name}`}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-gold)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-gold)] transition-colors flex items-center justify-center gap-1"
              >
                <Copy className="w-4 h-4" />
                {t.common.copy}
              </button>
              <div className="relative flex items-center gap-0.5 flex-shrink-0" role="group" aria-label="Taste preferences">
                {signalToast && (
                  <span className="absolute bottom-full right-0 mb-1.5 px-2 py-1 rounded-md bg-[var(--bg-elevated)] border border-[var(--border)] text-xs whitespace-nowrap animate-fade-in z-10 text-[var(--text-secondary)]" role="status">
                    {signalToast}
                  </span>
                )}
                <button
                  onClick={(e) => handleSignal(e, 'like')}
                  aria-label="More like this"
                  aria-pressed={signal === 'like'}
                  className={`p-2 rounded-lg transition-colors ${signal === 'like' ? 'text-[var(--accent-teal)] bg-[var(--accent-teal)]/10' : 'text-[var(--text-secondary)]/40 hover:text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10'}`}
                >
                  <ThumbsUp className="w-3.5 h-3.5" style={signal === 'like' ? { fill: 'currentColor' } : undefined} />
                </button>
                <button
                  onClick={(e) => handleSignal(e, 'dislike')}
                  aria-label="Less like this"
                  aria-pressed={signal === 'dislike'}
                  className={`p-2 rounded-lg transition-colors ${signal === 'dislike' ? 'text-[var(--status-amber)] bg-[var(--status-amber-bg)]' : 'text-[var(--text-secondary)]/40 hover:text-[var(--status-amber)] hover:bg-[var(--status-amber-bg)]'}`}
                >
                  <ThumbsDown className="w-3.5 h-3.5" style={signal === 'dislike' ? { fill: 'currentColor' } : undefined} />
                </button>
              </div>
            </div>
          )}
        </div>

        {isExpanded && (place.location_lat && place.location_lng) && (
          <div className="border-t border-[var(--border)] p-3 space-y-3">
            <div className="w-full h-48 rounded-lg overflow-hidden">
              <img
                src={staticMapUrl(place.location_lat, place.location_lng)}
                alt={`Map showing location of ${place.name}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  (e.currentTarget.nextElementSibling as HTMLElement)?.style.setProperty('display', 'flex');
                }}
              />
              <div className="w-full h-full bg-[var(--bg-elevated)] items-center justify-center text-4xl text-[var(--text-secondary)]" style={{ display: 'none' }}>
                📍
              </div>
            </div>

            <div className="space-y-2">
              {(place.formatted_address || place.address) && (
                <p className="text-sm text-[var(--text-secondary)]">
                  <span className="font-medium text-[var(--text-primary)]">{t.results.address}:</span> {place.formatted_address || place.address}
                </p>
              )}

              {place.opening_hours?.weekdayDescriptions && (
                <div className="text-sm text-[var(--text-secondary)]">
                  <span className="font-medium text-[var(--text-primary)]">{t.results.hoursToday}:</span>
                  <div className="mt-1">
                    {place.opening_hours.weekdayDescriptions[getDestinationDay()]}
                  </div>
                </div>
              )}

              {place.website_uri && (
                <a
                  href={place.website_uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[var(--accent-teal)] hover:underline inline-block"
                >
                  {t.results.visitWebsite} →
                </a>
              )}
            </div>

            <div className="flex gap-2 items-center" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={handleNavigate}
                aria-label={`Navigate to ${place.name}`}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-teal)] transition-colors flex items-center justify-center gap-1"
              >
                <Map className="w-4 h-4" />
                {t.common.navigate}
              </button>
              <button
                onClick={handleCopy}
                aria-label={`Copy address of ${place.name}`}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-gold)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-gold)] transition-colors flex items-center justify-center gap-1"
              >
                <Copy className="w-4 h-4" />
                {t.common.copy}
              </button>
              <div className="relative flex items-center gap-0.5 flex-shrink-0" role="group" aria-label="Taste preferences">
                <button
                  onClick={(e) => handleSignal(e, 'like')}
                  aria-label="More like this"
                  aria-pressed={signal === 'like'}
                  className={`p-2 rounded-lg transition-colors ${signal === 'like' ? 'text-[var(--accent-teal)] bg-[var(--accent-teal)]/10' : 'text-[var(--text-secondary)]/40 hover:text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10'}`}
                >
                  <ThumbsUp className="w-3.5 h-3.5" style={signal === 'like' ? { fill: 'currentColor' } : undefined} />
                </button>
                <button
                  onClick={(e) => handleSignal(e, 'dislike')}
                  aria-label="Less like this"
                  aria-pressed={signal === 'dislike'}
                  className={`p-2 rounded-lg transition-colors ${signal === 'dislike' ? 'text-[var(--status-amber)] bg-[var(--status-amber-bg)]' : 'text-[var(--text-secondary)]/40 hover:text-[var(--status-amber)] hover:bg-[var(--status-amber-bg)]'}`}
                >
                  <ThumbsDown className="w-3.5 h-3.5" style={signal === 'dislike' ? { fill: 'currentColor' } : undefined} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showToast && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg px-4 py-3 shadow-lg z-50 animate-fade-in">
          <p className="text-sm text-[var(--text-primary)]">{t.common.copiedToClipboard}</p>
        </div>
      )}
    </>
  );
}

function NewPlacePOICard({
  place,
  transport,
  isSelected,
  isSaving,
  onToggleSelect
}: {
  place: RankedPlace;
  transport: string;
  isSelected: boolean;
  isSaving?: boolean;
  onToggleSelect: (id: string) => void;
}) {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    const travelMode = transport === 'walking' ? 'walking' : transport === 'transit' ? 'transit' : 'driving';
    const destination = `${place.location.lat},${place.location.lng}`;
    const placeId = encodeURIComponent(place.id);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}&destination_place_id=${placeId}&travelmode=${travelMode}`, '_blank');
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = `${place.name}\n${place.formattedAddress}`;
    navigator.clipboard.writeText(text);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  // weekdayDescriptions is Mon-Sun (0-6), JS getDay() is Sun(0)-Sat(6)
  const getDestinationDay = () => {
    const day = new Date().getDay();
    return day === 0 ? 6 : day - 1;
  };

  const openStatus = place.currentOpeningHours ? {
    isOpen: place.currentOpeningHours.openNow,
    status: place.currentOpeningHours.openNow ? t.poi.open : t.poi.closed
  } : null;

  const categoryEmoji = place.category?.toLowerCase().includes('food') || place.category?.toLowerCase().includes('restaurant') ? '🍽️'
    : place.category?.toLowerCase().includes('bar') ? '🍺'
    : place.category?.toLowerCase().includes('museum') ? '🏛️'
    : place.category?.toLowerCase().includes('park') ? '🌳'
    : '📍';

  return (
    <>
      <div className="glass-morphism rounded-2xl overflow-hidden card-hover border-2 border-dashed border-[var(--accent-gold)]/40">
        <div className="p-4">
          <div className="flex gap-3">
            <div className="flex flex-col items-center gap-1 pt-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isSaving) onToggleSelect(place.id);
                }}
                disabled={isSaving}
                aria-label={isSelected ? `Remove ${place.name} from trip` : `Add ${place.name} to trip`}
                aria-pressed={isSelected}
                className={`w-7 h-7 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--bg-primary)] ${
                  isSaving
                    ? 'bg-[var(--accent-teal)]/50 border-[var(--accent-teal)]/50 cursor-wait'
                    : isSelected
                    ? 'bg-[var(--accent-teal)] border-[var(--accent-teal)] focus:ring-[var(--accent-teal)]'
                    : 'border-[var(--text-secondary)] bg-[var(--bg-elevated)] hover:border-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10 focus:ring-[var(--accent-gold)]'
                }`}
              >
                {isSaving ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : isSelected ? <Check className="w-5 h-5 text-white" /> : null}
              </button>
              <span className="text-[10px] text-[var(--text-secondary)] text-center leading-tight w-14">
                {isSaving ? (t.results.saving || 'Saving...') : isSelected ? t.results.saved : t.results.iWannaGo}
              </span>
            </div>

            <button
              type="button"
              className="flex gap-4 flex-1 cursor-pointer text-left bg-transparent border-0 p-0 appearance-none"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
              aria-label={`${place.name}, ${isExpanded ? 'collapse' : 'expand'} details`}
            >
              <div className="w-20 h-20 rounded-lg bg-[var(--bg-elevated)] flex items-center justify-center text-3xl flex-shrink-0 overflow-hidden">
                {place.photos && place.photos.length > 0 ? (
                  <>
                    <img
                      src={placePhotoUrl(place.photos[0])}
                      alt={place.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        (e.currentTarget.nextElementSibling as HTMLElement)?.style.setProperty('display', 'inline');
                      }}
                    />
                    <span style={{ display: 'none' }}>{categoryEmoji}</span>
                  </>
                ) : (
                  <span>{categoryEmoji}</span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-semibold text-sm text-[var(--text-primary)] leading-tight line-clamp-2">
                    {place.name}
                  </h3>
                  {place.rating && (
                    <div className="flex items-center gap-0.5 text-[var(--accent-gold)] flex-shrink-0">
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <span className="text-xs">{formatRating(place.rating)}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-[var(--accent-teal)]/20 text-[var(--accent-teal)] text-[11px] font-medium">
                    {place.category}
                  </span>
                  {place.matchingVibes && (Array.isArray(place.matchingVibes) ? place.matchingVibes : [place.matchingVibes]).length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      {(Array.isArray(place.matchingVibes) ? place.matchingVibes : [place.matchingVibes]).map(vibe => {
                        const display = vibeDisplay(t, vibe);
                        if (!display) return null;
                        const isCustom = vibe.startsWith('custom:');
                        return isCustom ? (
                          <span
                            key={vibe}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/25 border border-amber-500/40 text-[var(--accent-gold)] text-[11px] font-medium"
                            title={display.label}
                          >
                            <span>{display.emoji}</span>
                            <span>{display.label}</span>
                          </span>
                        ) : (
                          <span
                            key={vibe}
                            className="text-sm"
                            title={display.label}
                          >
                            {display.emoji}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                <p className="text-xs text-[var(--text-secondary)] mb-1 whitespace-pre-line line-clamp-2">
                  {place.hook}
                </p>

                <div className="flex items-center gap-2 text-[11px] text-[var(--text-secondary)]">
                  <span className="flex items-center gap-0.5">
                    <Clock className="w-3 h-3" />
                    {place.estimatedDuration}
                  </span>
                  <span>{place.distanceNote}</span>
                  {openStatus && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${openStatus.isOpen ? 'bg-[var(--status-green-bg)] text-[var(--status-green)]' : 'bg-[var(--status-amber-bg)] text-[var(--status-amber)]'}`}>
                      {openStatus.status}
                    </span>
                  )}
                </div>
              </div>
            </button>
          </div>

          {!isExpanded && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleNavigate}
                aria-label={`Navigate to ${place.name}`}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-teal)] transition-colors flex items-center justify-center gap-1"
              >
                <Map className="w-4 h-4" />
                {t.common.navigate}
              </button>
              <button
                onClick={handleCopy}
                aria-label={`Copy address of ${place.name}`}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-gold)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-gold)] transition-colors flex items-center justify-center gap-1"
              >
                <Copy className="w-4 h-4" />
                {t.common.copy}
              </button>
            </div>
          )}
        </div>

        {isExpanded && (
          <div className="border-t border-[var(--border)] p-4 space-y-4">
            <div className="w-full h-48 rounded-lg overflow-hidden">
              <img
                src={staticMapUrl(place.location.lat, place.location.lng)}
                alt={`Map showing location of ${place.name}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  (e.currentTarget.nextElementSibling as HTMLElement)?.style.setProperty('display', 'flex');
                }}
              />
              <div className="w-full h-full bg-[var(--bg-elevated)] items-center justify-center text-4xl text-[var(--text-secondary)]" style={{ display: 'none' }}>
                📍
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-[var(--text-secondary)]">
                <span className="font-medium text-[var(--text-primary)]">{t.results.address}:</span> {place.formattedAddress}
              </p>

              {place.currentOpeningHours && place.currentOpeningHours.weekdayDescriptions && (
                <div className="text-sm text-[var(--text-secondary)]">
                  <span className="font-medium text-[var(--text-primary)]">{t.results.hoursToday}:</span>
                  <div className="mt-1">
                    {place.currentOpeningHours.weekdayDescriptions[getDestinationDay()]}
                  </div>
                </div>
              )}

              {place.websiteUri && (
                <a
                  href={place.websiteUri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[var(--accent-teal)] hover:underline inline-block"
                >
                  {t.results.visitWebsite} →
                </a>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleNavigate}
                aria-label={`Navigate to ${place.name}`}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-teal)] transition-colors flex items-center justify-center gap-1"
              >
                <Map className="w-4 h-4" />
                {t.common.navigate}
              </button>
              <button
                onClick={handleCopy}
                aria-label={`Copy address of ${place.name}`}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-gold)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-gold)] transition-colors flex items-center justify-center gap-1"
              >
                <Copy className="w-4 h-4" />
                {t.common.copy}
              </button>
            </div>
          </div>
        )}
      </div>

      {showToast && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg px-4 py-3 shadow-lg z-50 animate-fade-in">
          <p className="text-sm text-[var(--text-primary)]">{t.common.copiedToClipboard}</p>
        </div>
      )}
    </>
  );
}

function TripListItem({
  itinerary,
  onSelect,
  onDelete,
  onUnpin,
  onRename,
  onShare,
  isDeleting,
  isArchived,
  onUpgrade,
  mergeMode,
  isMergeSelected,
  onMergeToggle
}: {
  itinerary: SavedItinerary;
  onSelect: () => void;
  onDelete: () => void;
  /** When provided (pinned trip in "Your Plan" box), the trash icon is
   *  replaced by an Unpin icon — pinned trips are protected from accidental
   *  delete; user must unpin first to delete from the bucket view. */
  onUnpin?: () => void;
  onRename: (newTitle: string) => Promise<boolean>;
  onShare?: () => void | Promise<void>;
  isDeleting: boolean;
  isArchived?: boolean;
  onUpgrade?: () => void;
  mergeMode?: boolean;
  isMergeSelected?: boolean;
  onMergeToggle?: () => void;
}) {
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(itinerary.title);
  const [isSaving, setIsSaving] = useState(false);
  const standardVibeEmojis = itinerary.vibes.filter(v => !v.startsWith('custom:')).map(v => VIBE_EMOJIS[v] || '').join('');
  const customVibes = itinerary.vibes.filter(v => v.startsWith('custom:'));
  const timeLabel = t.time[itinerary.time_available as keyof typeof t.time] || itinerary.time_available;

  const formatDate = (dateStr: string) => formatDateShared(dateStr, { weekday: true });
  const formatTime = (dateStr: string) => formatTimeShared(dateStr);

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditTitle(itinerary.title);
    setIsEditing(true);
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditTitle(itinerary.title);
  };

  const handleSaveEdit = async (e: React.MouseEvent | React.FormEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!editTitle.trim() || editTitle.trim() === itinerary.title) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    const success = await onRename(editTitle.trim());
    setIsSaving(false);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditTitle(itinerary.title);
    }
  };

  return (
    <div
      className={`relative glass-morphism rounded-xl p-4 cursor-pointer transition-all ${
        isArchived
          ? 'opacity-60 border-[var(--border)]'
          : isMergeSelected
          ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5'
          : 'hover:border-[var(--accent-teal)]/40'
      }`}
      role="button"
      tabIndex={isEditing ? -1 : 0}
      aria-label={isArchived ? `${itinerary.title}, archived` : mergeMode ? `${itinerary.title}, ${isMergeSelected ? 'selected' : 'not selected'} for merge` : itinerary.title}
      onClick={isEditing ? undefined : isArchived && onUpgrade ? onUpgrade : mergeMode ? onMergeToggle : onSelect}
      onKeyDown={(e) => { if (isEditing) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (isArchived && onUpgrade ? onUpgrade : mergeMode ? onMergeToggle : onSelect)?.(); } }}
    >
      <div className="flex items-start justify-between">
        {mergeMode && (
          <div
            className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mr-3 mt-0.5 transition-all ${
              isMergeSelected
                ? 'bg-[var(--accent-teal)] border-[var(--accent-teal)]'
                : 'border-[var(--accent-teal)]/60 bg-[var(--bg-elevated)] merge-checkbox-throb'
            }`}
            role="checkbox"
            aria-checked={isMergeSelected}
            aria-label={`Select ${itinerary.title} for merge`}
          >
            {isMergeSelected && <Check className="w-4 h-4 text-white" />}
          </div>
        )}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <form onSubmit={handleSaveEdit} className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                className="flex-1 px-2 py-1 rounded-lg bg-[var(--bg-elevated)] border border-[var(--accent-teal)] text-[var(--text-primary)] text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[var(--accent-teal)]/50"
              />
              <button
                type="submit"
                disabled={isSaving}
                aria-label="Save title"
                className="p-1.5 rounded-lg text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10 transition-colors disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                aria-label="Cancel editing"
                className="p-1.5 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--status-red-bg)] hover:text-[var(--status-red)] transition-colors"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-1.5 min-w-0">
              <h3 className="font-semibold text-lg text-[var(--text-primary)] truncate">
                {itinerary.title}
              </h3>
              {isArchived ? (
                <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[var(--accent-gold)]/15 border border-[var(--accent-gold)]/30 text-[var(--accent-gold)] text-[11px] font-medium">
                  <Lock className="w-2.5 h-2.5" />
                  Archived
                </span>
              ) : !mergeMode && (
                <button
                  onClick={handleStartEdit}
                  aria-label={`Rename ${itinerary.title}`}
                  className="flex-shrink-0 p-1 rounded text-[var(--text-secondary)]/40 hover:text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10 transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1 text-xs text-[var(--text-secondary)] flex-wrap">
            <span>{TRANSPORT_ICONS[itinerary.transport]}</span>
            <span>{timeLabel}</span>
            {(standardVibeEmojis || customVibes.length > 0) && (
              <>
                <span className="text-[var(--text-secondary)]/40">|</span>
                {standardVibeEmojis && <span>{standardVibeEmojis}</span>}
                {customVibes.map(v => (
                  <span key={v} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/25 border border-amber-500/40 text-[var(--accent-gold)] text-xs font-medium">
                    ✨ {v.slice(7)}
                  </span>
                ))}
              </>
            )}
          </div>
          <div className="flex items-center gap-1 mt-1 text-xs text-[var(--text-secondary)]/70 flex-wrap">
            <Clock className="w-3 h-3" />
            <span>{formatDate(itinerary.created_at)}</span>
            <span className="opacity-50">@</span>
            <span>{formatTime(itinerary.created_at)}</span>
            <span className="mx-1">·</span>
            <span>{itinerary.places.length} {t.savedTrips.places}</span>
            {itinerary.ai_mode && itinerary.ai_mode !== 'off' && (
              <>
                <span className="mx-1">·</span>
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-[var(--accent-teal)]/25 border border-[var(--accent-teal)]/40 text-[var(--accent-teal)] text-[11px] font-medium uppercase tracking-wide">
                  AI {itinerary.ai_mode}
                </span>
              </>
            )}
            {itinerary.ai_mode === 'off' && (
              <>
                <span className="mx-1">·</span>
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-[var(--text-secondary)]/10 border border-[var(--text-secondary)]/20 text-[var(--text-secondary)] text-[11px] font-medium uppercase tracking-wide">
                  No AI
                </span>
              </>
            )}
          </div>
          {itinerary.places.length > 0 && (
            <p className="text-xs text-[var(--text-secondary)]/60 truncate mt-1">
              {itinerary.places.slice(0, 3).map(p => p.name).join(', ')}{itinerary.places.length > 3 ? '...' : ''}
            </p>
          )}
        </div>
        {!isEditing && (
          <div className="flex flex-col items-end justify-between self-stretch flex-shrink-0 ml-1">
            {isArchived ? (
              <Crown className="w-4 h-4 text-[var(--accent-gold)]" />
            ) : !mergeMode && onShare ? (
              /* Combined Share/Shared affordance — right-justified.
                 When not shared: icon-only button with hover-border affordance.
                 When shared: bordered teal pill with "Shared" label, still
                 clickable to re-share / copy link. Unshare removed from this
                 view per Ian — user can unshare from the expanded trip view. */
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onShare();
                }}
                aria-label={`Share ${itinerary.title}`}
                className={`inline-flex items-center gap-1 rounded-lg transition-colors ${
                  itinerary.is_shared
                    ? 'px-2 py-1 border border-[var(--accent-teal)]/40 bg-[var(--accent-teal)]/15 text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/25'
                    : 'p-1.5 border border-transparent text-[var(--text-secondary)]/50 hover:text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10 hover:border-[var(--accent-teal)]/30'
                }`}
              >
                <Share2 className="w-3.5 h-3.5" />
                {itinerary.is_shared && (
                  <span className="text-[11px] font-medium">Shared</span>
                )}
              </button>
            ) : (
              <span />
            )}
            {!isArchived && !mergeMode ? (
              onUnpin ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnpin();
                  }}
                  aria-label={`Unpin ${itinerary.title} from your plan`}
                  className="p-1.5 rounded-lg text-[var(--accent-teal)] hover:text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10 transition-colors"
                >
                  <PinOff className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  disabled={isDeleting}
                  aria-label={`Delete ${itinerary.title}`}
                  className="p-1.5 rounded-lg text-[var(--text-secondary)]/50 hover:text-[var(--status-red)] hover:bg-[var(--status-red-bg)] transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )
            ) : (
              <span />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function GroupHeader({
  label,
  count,
  isExpanded,
  onToggle,
  onDelete,
  isDeleting,
  isArchived
}: {
  label: string;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  isDeleting: boolean;
  isArchived?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div className="flex items-center justify-between py-2">
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-label={`${label}, ${count} trips${isArchived ? ', archived' : ''}`}
        className="flex items-center gap-2 text-[var(--text-primary)] hover:text-[var(--accent-teal)] transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="w-5 h-5" />
        ) : (
          <ChevronRight className="w-5 h-5" />
        )}
        <span className="font-semibold text-lg">{label}</span>
        <span className="text-sm text-[var(--text-secondary)]">({count} {t.savedTrips.trips})</span>
        {isArchived && (
          <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[var(--accent-gold)]/15 border border-[var(--accent-gold)]/30 text-[var(--accent-gold)] text-[11px] font-medium">
            <Lock className="w-2.5 h-2.5" />
            Archived
          </span>
        )}
      </button>
      <button
        onClick={onDelete}
        disabled={isDeleting}
        aria-label={`Delete ${label} group`}
        className="text-xs text-[var(--text-secondary)] hover:text-[var(--status-red)] transition-colors disabled:opacity-50"
      >
        {t.savedTrips.deleteGroup}
      </button>
    </div>
  );
}

export function SavedTripsScreen({
  groupedItineraries,
  onBackToPlanning,
  onDeleteItinerary,
  onDeleteGroup,
  onDeletePlace,
  onFindMorePlaces,
  onAddPlacesToTrip,
  onRenameTrip,
  onUnsaveNewPlace,
  onShareTrip,
  onUnshareTrip,
  loading,
  loadingMore,
  groupingType,
  onGroupingChange,
  isPremium,
  selectNewestOnOpen,
  resetToSummary,
  onUpgrade,
  onMergeTrips,
  onSetPinned,
  onUnpin,
  onReevaluationSaved
}: SavedTripsScreenProps) {
  const { t, language, setLanguage } = useLanguage();
  const feedback = useUiFeedbackContext();
  const { user } = useAuth();
  const [selectedItinerary, setSelectedItinerary] = useState<SavedItinerary | null>(null);
  // H-214: Reevaluate on a logged-in user's own saved trip. Mirrors the anon
  // flow in SharedTripPage — panel opens from the Sparkles button in the
  // detail-view top row; on result we render the reevaluated places in
  // place of the original, with a Save (fork to new trip) / Discard banner.
  const [reevaluatePanelOpen, setReevaluatePanelOpen] = useState(false);
  const [reevaluatedPlaces, setReevaluatedPlaces] = useState<ReevaluatedPlace[] | null>(null);
  const [reevaluateContext, setReevaluateContext] = useState<ReevaluateContext | null>(null);
  const [savingReevaluation, setSavingReevaluation] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletingGroupKey, setDeletingGroupKey] = useState<string | null>(null);
  const [newPlaces, setNewPlaces] = useState<RankedPlace[]>([]);
  const [selectedNewPlaceIds, setSelectedNewPlaceIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [hasInitializedExpanded, setHasInitializedExpanded] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelectedIds, setMergeSelectedIds] = useState<Set<string>>(new Set());
  const [isMerging, setIsMerging] = useState(false);
  const [isEditingDetailTitle, setIsEditingDetailTitle] = useState(false);
  const [detailTitleDraft, setDetailTitleDraft] = useState('');
  const detailTitleRef = useRef<HTMLInputElement>(null);
  const [pendingDeleteTrip, setPendingDeleteTrip] = useState<{ id: string; title: string } | null>(null);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState<GroupedItineraries | null>(null);
  const pendingDeleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDeleteIdRef = useRef<string | null>(null);
  // Tracks whether we've already auto-opened the newest trip this mount
  const hasAutoSelectedRef = useRef(false);
  const [confirmUnshare, setConfirmUnshare] = useState<{ id: string; title: string } | null>(null);
  const [isUnsharing, setIsUnsharing] = useState(false);

  // Reset to summary view when tab is re-tapped
  useEffect(() => {
    if (resetToSummary && resetToSummary > 0) {
      setSelectedItinerary(null);
      setNewPlaces([]);
      setSelectedNewPlaceIds(new Set());
      setMergeMode(false);
      setMergeSelectedIds(new Set());
    }
  }, [resetToSummary]);

  // Compute group counts for both grouping types
  const { dayGroupCount, locationGroupCount } = useMemo(() => {
    const allItineraries = groupedItineraries.flatMap(g => g.itineraries);
    const dayKeys = new Set(allItineraries.map(i => {
      const d = new Date(i.created_at);
      return localDayKey(d);
    }));
    const locationKeys = new Set(allItineraries.map(i => {
      const parts = i.location.split(',').map(p => p.trim());
      return (parts.length >= 3 ? parts[1] : parts[0]).toLowerCase();
    }));
    return { dayGroupCount: dayKeys.size, locationGroupCount: locationKeys.size };
  }, [groupedItineraries]);

  const sortedGroups = useMemo(() => {
    // H-213 v2 (Ian feedback): pinned trips are surfaced in the "Your plan" box
    // and must NOT also appear in their date/location bucket — that was a
    // duplicate display. Filter pinned itineraries out here; drop any group
    // that becomes empty as a result.
    return [...groupedItineraries]
      .map(g => ({ ...g, itineraries: g.itineraries.filter(i => !i.is_pinned) }))
      .filter(g => g.itineraries.length > 0)
      .sort((a, b) => {
        const aLatest = Math.max(...a.itineraries.map(i => new Date(i.created_at).getTime()));
        const bLatest = Math.max(...b.itineraries.map(i => new Date(i.created_at).getTime()));
        return bLatest - aLatest;
      });
  }, [groupedItineraries]);

  useEffect(() => {
    if (selectedItinerary) {
      for (const group of groupedItineraries) {
        const updated = group.itineraries.find(i => i.id === selectedItinerary.id);
        if (updated) {
          setSelectedItinerary(updated);
          return;
        }
      }
    }
  }, [groupedItineraries]);

  // H-214: Clear any pending reevaluation when the user switches trips
  // or backs out of detail view. Stops a previous reevaluation from
  // bleeding into the next trip opened.
  useEffect(() => {
    setReevaluatedPlaces(null);
    setReevaluateContext(null);
    setReevaluatePanelOpen(false);
  }, [selectedItinerary?.id]);

  const handleReevaluated = (results: ReevaluatedPlace[], context: ReevaluateContext) => {
    setReevaluatedPlaces(results);
    setReevaluateContext(context);
    setReevaluatePanelOpen(false);
  };

  const handleDiscardReevaluation = () => {
    setReevaluatedPlaces(null);
    setReevaluateContext(null);
  };

  const handleSaveReevaluationAsNewTrip = async () => {
    if (!user?.id || !selectedItinerary || !reevaluatedPlaces || !reevaluateContext) return;
    setSavingReevaluation(true);
    try {
      const result = await saveReevaluatedTrip(
        user.id,
        selectedItinerary.id,
        reevaluateContext,
        reevaluatedPlaces,
      );
      if (!result) return;
      setReevaluatedPlaces(null);
      setReevaluateContext(null);
      if (onReevaluationSaved) await onReevaluationSaved();
    } finally {
      setSavingReevaluation(false);
    }
  };

  // H-214: Same shape as a normal SavedPlace[] used by the places-list
  // renderer below — built from ReevaluatedPlace[] when a reevaluation
  // is live so the existing SavedPOICard rendering works unchanged. The
  // reevaluated hooks and travel-distance notes replace the originals,
  // and sort_order drives the new display order.
  const displayPlaces: SavedPlace[] = useMemo(() => {
    if (!selectedItinerary) return [];
    if (!reevaluatedPlaces) return selectedItinerary.places;
    return reevaluatedPlaces
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(r => ({
        ...r.source,
        sort_order: r.sort_order,
        hook: r.hook,
        distance_note: r.distanceNote,
      }));
  }, [selectedItinerary, reevaluatedPlaces]);

  // When arriving via "View Trip" or post-auth save, auto-open the newest trip
  // once data has finished loading. Uses a ref so it only fires once per mount.
  useEffect(() => {
    if (
      selectNewestOnOpen &&
      !hasAutoSelectedRef.current &&
      !loading &&
      sortedGroups.length > 0
    ) {
      const newestTrip = sortedGroups[0].itineraries[0];
      if (newestTrip) {
        hasAutoSelectedRef.current = true;
        setSelectedItinerary(newestTrip);
      }
    }
  }, [selectNewestOnOpen, loading, sortedGroups]);

  if (!hasInitializedExpanded && sortedGroups.length > 0) {
    setExpandedGroups(new Set([sortedGroups[0].groupKey]));
    setHasInitializedExpanded(true);
  }

  const handleSelectItinerary = (itinerary: SavedItinerary) => {
    setSelectedItinerary(itinerary);
    setNewPlaces([]);
    setSelectedNewPlaceIds(new Set());
  };

  const handleBack = () => {
    setSelectedItinerary(null);
    setNewPlaces([]);
    setSelectedNewPlaceIds(new Set());
    setIsEditingDetailTitle(false);
  };

  const commitPendingDelete = () => {
    if (pendingDeleteTimerRef.current && pendingDeleteIdRef.current) {
      clearTimeout(pendingDeleteTimerRef.current);
      onDeleteItinerary(pendingDeleteIdRef.current);
      pendingDeleteTimerRef.current = null;
      pendingDeleteIdRef.current = null;
    }
  };

  // Commit any pending delete when the screen unmounts (user navigated away)
  useEffect(() => {
    return () => { commitPendingDelete(); };
  }, []);

  const handleDelete = (id: string, title: string) => {
    // If a delete is already pending, commit it immediately before starting a new one
    commitPendingDelete();

    // Hide the trip immediately in the UI
    setPendingDeleteTrip({ id, title });
    pendingDeleteIdRef.current = id;

    if (selectedItinerary?.id === id) {
      setSelectedItinerary(null);
    }

    // Schedule the actual DB delete after 5s
    pendingDeleteTimerRef.current = setTimeout(async () => {
      pendingDeleteTimerRef.current = null;
      pendingDeleteIdRef.current = null;
      setPendingDeleteTrip(null);
      setDeletingId(id);
      await onDeleteItinerary(id);
      setDeletingId(null);
    }, 5000);
  };

  const handleUndoDelete = () => {
    if (pendingDeleteTimerRef.current) {
      clearTimeout(pendingDeleteTimerRef.current);
      pendingDeleteTimerRef.current = null;
      pendingDeleteIdRef.current = null;
    }
    setPendingDeleteTrip(null);
  };

  const handleDeleteGroup = async (group: GroupedItineraries) => {
    setDeletingGroupKey(group.groupKey);
    await onDeleteGroup(group.itineraries.map(i => i.id));
    setDeletingGroupKey(null);
    setConfirmDeleteGroup(null);
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const TIME_ORDER = ['1-2-hrs', 'half-day', 'full-day', '2-3-days'];
  const TRANSPORT_ORDER = ['walking', 'transit', 'taxi'];
  const AI_MODE_ORDER = ['off', 'low', 'medium', 'high'];

  // Pick the "larger" enum value across a set (for merging time / transport / AI mode).
  const maxByOrder = (arr: string[], vals: string[]): string => {
    let max = -1;
    for (const v of vals) {
      const idx = arr.indexOf(v);
      if (idx > max) max = idx;
    }
    return max >= 0 ? arr[max] : vals[0];
  };

  const toggleMergeSelection = (id: string) => {
    setMergeSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
        // If nothing selected, exit merge mode
        if (newSet.size === 0) {
          setMergeMode(false);
        }
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const cancelMergeMode = () => {
    setMergeMode(false);
    setMergeSelectedIds(new Set());
  };

  // H-213 v2 pin/unpin handlers. No undo flow — up to 2 pinned trips, new
  // pins displace the most-recent one (slot 2).
  const handlePinTrip = async (itin: SavedItinerary) => {
    if (!onSetPinned) return;
    const ok = await onSetPinned(itin.id);
    if (!ok) return;
    // After pinning from a saved-trip detail view, return to the summary so
    // the user immediately sees the pinned tile in the "Your Plan" box.
    if (selectedItinerary?.id === itin.id) {
      setSelectedItinerary(null);
    }
  };

  const handleUnpinTrip = async (itin: SavedItinerary) => {
    if (!onUnpin) return;
    const success = await onUnpin(itin.id);
    if (success && selectedItinerary?.id === itin.id) {
      setSelectedItinerary({ ...selectedItinerary, is_pinned: false });
    }
  };

  // Merge Plan: when both pin slots are filled, this CTA bypasses
  // selection-mode entirely and merges the two pinned trips immediately.
  // Title rule (per Ian): only-one-renamed → that title; else first-pinned wins.
  const handleMergePinnedTrips = async () => {
    if (!onMergeTrips) return;
    const allTrips = groupedItineraries.flatMap(g => g.itineraries);
    const pinned = allTrips
      .filter(t => t.is_pinned)
      .sort((a, b) => {
        const aT = a.pinned_at ? new Date(a.pinned_at).getTime() : 0;
        const bT = b.pinned_at ? new Date(b.pinned_at).getTime() : 0;
        return aT - bT;
      });
    if (pinned.length < 2) return;
    const [first, second] = pinned;

    setIsMerging(true);

    const sourceIds = [second.id];
    const mergedTransport = maxByOrder(TRANSPORT_ORDER, [first.transport, second.transport]);
    const mergedTime = maxByOrder(TIME_ORDER, [first.time_available, second.time_available]);
    const mergedAiMode = maxByOrder(AI_MODE_ORDER, [first.ai_mode, second.ai_mode]);
    const mergedVibes = [...new Set([...first.vibes, ...second.vibes])];
    const uniqueLocations = [...new Set([first.location, second.location])];
    const mergedLocation = uniqueLocations.join(', ');

    const seen = new Set<string>();
    const dedupedPlaces: SavedPlace[] = [];
    for (const p of [...first.places, ...second.places]) {
      if (!seen.has(p.place_id)) { seen.add(p.place_id); dedupedPlaces.push(p); }
    }

    // Renamed-detection (mirrors handleMerge logic)
    const renamedSources: SavedItinerary[] = [];
    for (const itin of [first, second]) {
      const autoTitle = generateTitle(
        itin.location,
        itin.vibes,
        itin.places.map(p => ({ id: p.place_id, name: p.name, matchingVibes: p.matching_vibes })) as unknown as RankedPlace[]
      );
      if (itin.title !== autoTitle) renamedSources.push(itin);
    }
    // Title: 1 renamed → that title; 0 or 2 renamed → first-pinned's title.
    const mergeTitle = renamedSources.length === 1 ? renamedSources[0].title : first.title;

    await onMergeTrips(first.id, sourceIds, {
      location: mergedLocation,
      time_available: mergedTime,
      transport: mergedTransport,
      vibes: mergedVibes,
      ai_mode: mergedAiMode,
      sourceLocations: [first.location, second.location],
      pinnedTitle: mergeTitle,
      pinned: true,
    }, dedupedPlaces);

    setTimeout(() => setIsMerging(false), 300);
  };

  const handleMerge = async () => {
    if (!onMergeTrips || mergeSelectedIds.size < 2) return;

    // Collect all selected itineraries
    const allItineraries = groupedItineraries.flatMap(g => g.itineraries);
    const selected = allItineraries.filter(i => mergeSelectedIds.has(i.id));
    if (selected.length < 2) return;

    setIsMerging(true);

    // Target is the first selected
    const target = selected[0];
    const sourceIds = selected.slice(1).map(i => i.id);

    // Merge parameters: pick the "larger" value
    const mergedTransport = maxByOrder(TRANSPORT_ORDER, selected.map(i => i.transport));
    const mergedTime = maxByOrder(TIME_ORDER, selected.map(i => i.time_available));
    const mergedAiMode = maxByOrder(AI_MODE_ORDER, selected.map(i => i.ai_mode));

    // Vibes: union
    const mergedVibes = [...new Set(selected.flatMap(i => i.vibes))];

    // Location: unique locations joined
    const uniqueLocations = [...new Set(selected.map(i => i.location))];
    const mergedLocation = uniqueLocations.join(', ');

    // De-dupe places by place_id, keep first occurrence
    const seenPlaceIds = new Set<string>();
    const dedupedPlaces: SavedPlace[] = [];
    for (const itin of selected) {
      for (const place of itin.places) {
        if (!seenPlaceIds.has(place.place_id)) {
          seenPlaceIds.add(place.place_id);
          dedupedPlaces.push(place);
        }
      }
    }

    // Detect user-renamed trips by comparing actual title to what generateTitle would produce
    const renamedTitles: { title: string; placeCount: number }[] = [];
    for (const itin of selected) {
      const autoTitle = generateTitle(
        itin.location,
        itin.vibes,
        itin.places.map(p => ({
          id: p.place_id,
          name: p.name,
          matchingVibes: p.matching_vibes
        })) as unknown as RankedPlace[]
      );
      if (itin.title !== autoTitle) {
        renamedTitles.push({ title: itin.title, placeCount: itin.places.length });
      }
    }

    // H-217 Rule 1: pinned source's title wins verbatim; merge stays pinned.
    const pinnedSource = selected.find(i => i.is_pinned);

    await onMergeTrips(target.id, sourceIds, {
      location: mergedLocation,
      time_available: mergedTime,
      transport: mergedTransport,
      vibes: mergedVibes,
      ai_mode: mergedAiMode,
      sourceLocations: selected.map(i => i.location),
      renamedTitles,
      pinnedTitle: pinnedSource?.title ?? null,
      pinned: Boolean(pinnedSource),
    }, dedupedPlaces);

    // Brief delay so the parent can reload itineraries before we reset merge UI
    setTimeout(() => {
      setIsMerging(false);
      cancelMergeMode();
    }, 300);
  };

  const [shareCopied, setShareCopied] = useState(false);

  const handleShareSelectedTrip = async () => {
    if (!selectedItinerary || !onShareTrip) return;
    const url = await onShareTrip(selectedItinerary.id);
    if (!url) return;
    // Mark the local state as shared so the UI updates immediately
    setSelectedItinerary({ ...selectedItinerary, is_shared: true });

    // Brief delay to let state settle — avoids native share API timing issues
    await new Promise(r => setTimeout(r, 100));

    if (navigator.share) {
      try {
        await navigator.share({
          title: selectedItinerary.title,
          text: `${selectedItinerary.title} — ${selectedItinerary.places.length} places, optimised for ${selectedItinerary.location}. Built with TripBoom:`,
          url
        });
        return;
      } catch {
        // User cancelled or native share failed — fall through to clipboard
      }
    }

    // Clipboard fallback (always runs if navigator.share unavailable or fails)
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  const handleFindMore = async () => {
    if (!selectedItinerary) return;
    const places = await onFindMorePlaces(selectedItinerary);
    const existingPlaceIds = new Set(selectedItinerary.places.map(p => p.place_id));
    const filtered = places.filter(p => !existingPlaceIds.has(p.id));
    setNewPlaces(filtered);
  };

  const [savingPlaceIds, setSavingPlaceIds] = useState<Set<string>>(new Set());

  const toggleNewPlaceSelection = async (placeId: string) => {
    if (!selectedItinerary) return;

    // Already saved — unsave (remove from trip)
    if (selectedNewPlaceIds.has(placeId)) {
      if (!onUnsaveNewPlace) return;
      setSavingPlaceIds(prev => new Set(prev).add(placeId));
      await onUnsaveNewPlace(selectedItinerary.id, placeId);
      setSelectedNewPlaceIds(prev => {
        const next = new Set(prev);
        next.delete(placeId);
        return next;
      });
      // Update local selectedItinerary to reflect the removal
      setSelectedItinerary(prev => prev ? {
        ...prev,
        places: prev.places.filter(p => p.place_id !== placeId)
      } : null);
      setSavingPlaceIds(prev => {
        const next = new Set(prev);
        next.delete(placeId);
        return next;
      });
      return;
    }

    // Not yet saved — save instantly
    const place = newPlaces.find(p => p.id === placeId);
    if (!place) return;

    setSavingPlaceIds(prev => new Set(prev).add(placeId));
    await onAddPlacesToTrip(selectedItinerary.id, [place]);
    setSelectedNewPlaceIds(prev => new Set(prev).add(placeId));
    setSavingPlaceIds(prev => {
      const next = new Set(prev);
      next.delete(placeId);
      return next;
    });
  };

  if (loading && groupedItineraries.length === 0) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--accent-teal)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <div className="sticky top-0 z-10 bg-[var(--bg-primary)]/95 backdrop-blur-sm border-b border-[var(--border)] pt-[env(safe-area-inset-top)]">
        <div className="max-w-2xl mx-auto px-4 py-3">
          {selectedItinerary ? (
            // H-213 v6 (Ian, 3rd time of asking): pin pill TRULY centered in the
            // top row of the saved-trip detail view. Use 3-column grid so the pin
            // is in the geometric centre, independent of share-group width.
            <div className="grid grid-cols-3 items-center gap-2">
              <div className="flex justify-start">
                <button
                  onClick={handleBack}
                  aria-label="Back to trip list"
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              </div>
              {/* Centre cell: Pin + Tune paired. Both are trip-verb
                  actions on the current trip; pairing keeps the visual
                  centre stable even when the right cluster grows with
                  the Unlink2 button on shared trips. */}
              <div className="flex justify-center items-center gap-1.5">
                {(onSetPinned || onUnpin) && (
                  selectedItinerary.is_pinned ? (
                    <button
                      onClick={() => handleUnpinTrip(selectedItinerary)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-[var(--accent-teal)]/20 text-[var(--accent-teal)] border border-[var(--accent-teal)]/40 hover:opacity-90 active:scale-95 transition-all"
                      aria-label={t.pin?.unpinAria || 'Remove this trip from your plan'}
                    >
                      <Pin className="w-4 h-4" style={{ fill: 'currentColor' }} />
                      <span className="whitespace-nowrap">{t.pin?.pinnedLabel || 'Pinned'}</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePinTrip(selectedItinerary)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border-2 border-dashed border-[var(--accent-teal)]/40 text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10 hover:border-[var(--accent-teal)]/70 active:scale-95 transition-all"
                      aria-label={t.pin?.pinAria || 'Add this trip to your plan'}
                    >
                      <PinOff className="w-4 h-4" />
                      <span className="whitespace-nowrap">{t.pin?.pinTripToPlanLabel || 'Pin to Plan'}</span>
                    </button>
                  )
                )}
                {/* H-214: Tune button — paired with Pin in the centre cell. */}
                <button
                  onClick={() => setReevaluatePanelOpen(true)}
                  aria-label={t.reevaluate?.savedTripButtonAria || 'Tune this trip for a new context'}
                  title={t.reevaluate?.savedTripButtonTitle || 'Tune'}
                  className="flex items-center gap-1 px-2.5 py-2 rounded-xl border border-[var(--accent-teal)]/30 text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10 hover:border-[var(--accent-teal)]/60 active:scale-95 transition-all"
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-semibold whitespace-nowrap">{t.reevaluate?.savedTripButtonLabel || 'Tune'}</span>
                </button>
              </div>
              <div className="flex justify-end">
                {onShareTrip ? (
                  <div className="flex items-center gap-1.5">
                    {selectedItinerary.is_shared && onUnshareTrip && (
                      <button
                        onClick={() => setConfirmUnshare({ id: selectedItinerary.id, title: selectedItinerary.title })}
                        className="p-2 rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--status-red)] hover:border-[var(--status-red)]/40 hover:bg-[var(--status-red-bg)] transition-all"
                        aria-label="Remove share link"
                      >
                        <Unlink2 className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={handleShareSelectedTrip}
                      aria-label="Share trip"
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold shadow-sm hover:opacity-90 active:scale-95 transition-all ${
                        selectedItinerary.is_shared
                          ? 'bg-[var(--accent-teal)]/20 text-[var(--accent-teal)] border border-[var(--accent-teal)]/40'
                          : 'bg-[var(--accent-teal)] text-white'
                      }`}
                    >
                      <Share2 className="w-4 h-4" />
                      Share
                    </button>
                  </div>
                ) : (
                  <div className="w-10" />
                )}
              </div>
            </div>
          ) : (
          <div className="flex items-center justify-between">
            <button
              onClick={onBackToPlanning}
              aria-label="Back to planning"
              className="w-10 h-10 rounded-lg flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <p className="flex-1 min-w-0 text-xl font-semibold tracking-tight text-[var(--text-primary)] mx-2">
              <>Saved<span style={{ background: 'linear-gradient(to right, #f59e0b, #2dd4bf)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Trips</span></>
            </p>
            {onMergeTrips && groupedItineraries.length > 0 ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={mergeMode
                    ? (mergeSelectedIds.size >= 2 ? handleMerge : undefined)
                    : () => setMergeMode(true)
                  }
                  disabled={isMerging || (mergeMode && mergeSelectedIds.size < 2 && mergeSelectedIds.size > 0)}
                  aria-label={isMerging ? 'Merging trips' : mergeMode ? (mergeSelectedIds.size >= 2 ? `Merge ${mergeSelectedIds.size} trips` : 'Select trips to merge') : 'Merge trips'}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                    isMerging
                      ? 'bg-[var(--accent-teal)] border-[var(--accent-teal)] text-white opacity-70 cursor-not-allowed'
                      : mergeMode && mergeSelectedIds.size >= 2
                      ? 'bg-[var(--accent-teal)] border-[var(--accent-teal)] text-white shadow-[0_0_20px_rgba(45,212,191,0.4)] animate-throb'
                      : mergeMode
                      ? 'bg-[var(--bg-elevated)] border-[var(--accent-teal)]/40 text-[var(--text-secondary)]'
                      : 'bg-[var(--bg-elevated)] border-[var(--border)] hover:bg-[var(--accent-teal)]/10 hover:border-[var(--accent-teal)]/40 text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  {isMerging ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Merge className="w-3.5 h-3.5" />
                  )}
                  {isMerging
                    ? 'Merging...'
                    : mergeMode
                    ? (mergeSelectedIds.size >= 2 ? t.savedTrips.merge : t.savedTrips.selectTrips)
                    : t.savedTrips.mergeTrips
                  }
                </button>
                {mergeMode && !isMerging && (
                  <button
                    onClick={cancelMergeMode}
                    aria-label="Cancel merge"
                    className="w-8 h-8 rounded-full bg-red-500 border border-red-500 text-white shadow-[0_0_12px_rgba(239,68,68,0.5)] hover:bg-red-400 hover:border-red-400 active:scale-95 transition-all flex items-center justify-center"
                  >
                    <XIcon className="w-4 h-4" strokeWidth={3} />
                  </button>
                )}
              </div>
            ) : (
              <div className="w-10" />
            )}
          </div>
          )}

          {/* Editable title row — detail view only */}
          {selectedItinerary && (
            <div className="mt-1.5">
              {isEditingDetailTitle ? (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const trimmed = detailTitleDraft.trim();
                    if (!trimmed || trimmed === selectedItinerary.title) {
                      setIsEditingDetailTitle(false);
                      return;
                    }
                    const success = await onRenameTrip(selectedItinerary.id, trimmed);
                    if (success) setSelectedItinerary({ ...selectedItinerary, title: trimmed });
                    setIsEditingDetailTitle(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <input
                    ref={detailTitleRef}
                    type="text"
                    value={detailTitleDraft}
                    onChange={(e) => setDetailTitleDraft(e.target.value)}
                    onBlur={async () => {
                      const trimmed = detailTitleDraft.trim();
                      if (trimmed && trimmed !== selectedItinerary.title) {
                        const success = await onRenameTrip(selectedItinerary.id, trimmed);
                        if (success) setSelectedItinerary({ ...selectedItinerary, title: trimmed });
                      }
                      setIsEditingDetailTitle(false);
                    }}
                    onKeyDown={(e) => { if (e.key === 'Escape') { setIsEditingDetailTitle(false); } }}
                    className="flex-1 text-base font-semibold tracking-tight text-[var(--text-primary)] bg-[var(--bg-elevated)] border border-[var(--accent-teal)]/40 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-[var(--accent-teal)]/40"
                    aria-label="Edit trip title"
                  />
                </form>
              ) : (
                <button
                  onClick={() => {
                    setDetailTitleDraft(selectedItinerary.title);
                    setIsEditingDetailTitle(true);
                    setTimeout(() => detailTitleRef.current?.focus(), 0);
                  }}
                  className="w-full text-left group flex items-center gap-1.5"
                  aria-label="Tap to edit trip title"
                >
                  <h2 className="text-base font-semibold tracking-tight text-[var(--text-primary)] leading-snug">
                    {selectedItinerary.title}
                  </h2>
                  <Pencil className="w-3.5 h-3.5 text-[var(--text-secondary)]/40 group-hover:text-[var(--accent-teal)] flex-shrink-0 transition-colors" />
                </button>
              )}
            </div>
          )}

          {selectedItinerary ? (<>
            <div className="flex items-center justify-center gap-2 flex-wrap text-xs text-[var(--text-secondary)] mt-2">
              <span>{selectedItinerary.places.length} {t.savedTrips.places}</span>
              <span>·</span>
              <span>{TRANSPORT_ICONS[selectedItinerary.transport]} {t.transport[selectedItinerary.transport as keyof typeof t.transport]}</span>
              {selectedItinerary.ai_mode && selectedItinerary.ai_mode !== 'off' && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[var(--accent-teal)]/25 border border-[var(--accent-teal)]/40 text-[var(--accent-teal)] text-[11px] font-medium uppercase tracking-wide">
                    AI {selectedItinerary.ai_mode}
                  </span>
                </>
              )}
              {selectedItinerary.ai_mode === 'off' && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[var(--text-secondary)]/10 border border-[var(--text-secondary)]/20 text-[var(--text-secondary)] text-[11px] font-medium uppercase tracking-wide">
                    No AI
                  </span>
                </>
              )}
              {(() => {
                const stdEmojis = selectedItinerary.vibes.filter(v => !v.startsWith('custom:')).map(v => VIBE_EMOJIS[v] || '').join('');
                const customs = selectedItinerary.vibes.filter(v => v.startsWith('custom:'));
                if (!stdEmojis && customs.length === 0) return null;
                return (
                  <>
                    <span>·</span>
                    {stdEmojis && <span>{stdEmojis}</span>}
                    {customs.map(v => (
                      <span key={v} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/25 border border-amber-500/40 text-[var(--accent-gold)] text-xs font-medium">
                        ✨ {v.slice(7)}
                      </span>
                    ))}
                  </>
                );
              })()}
            </div>
          </>) : null}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
        {selectedItinerary ? (
          <div className="space-y-4">
            {/* H-214: Banner shown while a reevaluation is overlaying the
                place list. Save creates a new (forked) trip; Discard reverts
                this view to the original. */}
            {reevaluatedPlaces && reevaluateContext && (
              <div className="p-3 rounded-xl bg-[var(--accent-teal)]/10 border border-[var(--accent-teal)]/30 flex items-center gap-3 animate-fade-in">
                <Sparkles className="w-5 h-5 text-[var(--accent-teal)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--accent-teal)]">
                    {reevaluatedPlaces?.some(p => p.rankingSource === 'offline') ? t.reevaluate.offlineBannerTitle : t.reevaluate.savedTripBannerTitle}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                    {`${reevaluateContext.locationLabel} · ${t.transport[reevaluateContext.transport as keyof typeof t.transport] || reevaluateContext.transport} · ${t.time[reevaluateContext.timeAvailable as keyof typeof t.time] || reevaluateContext.timeAvailable}`}
                  </p>
                </div>
                <button
                  onClick={handleDiscardReevaluation}
                  disabled={savingReevaluation}
                  aria-label={t.reevaluate?.savedTripDiscardAria || 'Discard reevaluation'}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] disabled:opacity-50 transition-colors flex-shrink-0"
                >
                  {t.reevaluate?.savedTripDiscard || 'Discard'}
                </button>
                <button
                  onClick={handleSaveReevaluationAsNewTrip}
                  disabled={savingReevaluation}
                  aria-label={t.reevaluate?.savedTripSaveAria || 'Save reevaluation as a new trip'}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[var(--accent-teal)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity flex-shrink-0 flex items-center gap-1.5"
                >
                  {savingReevaluation ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {t.reevaluate?.savedTripSave || 'Save as new trip'}
                </button>
              </div>
            )}
            {displayPlaces.map((place, index) => (
              <div key={place.id} className={index < 3 ? `animate-fade-in-delay-${index}` : 'animate-fade-in-delay-3'}>
                <SavedPOICard
                  place={place}
                  transport={reevaluateContext?.transport || selectedItinerary.transport}
                  onDelete={() => onDeletePlace(place.id, selectedItinerary.id)}
                />
              </div>
            ))}

            {newPlaces.length > 0 && (
              <>
                <div className="py-4 text-center">
                  <div className="inline-block px-4 py-2 rounded-full bg-[var(--accent-gold)]/10 border border-[var(--accent-gold)]/30">
                    <span className="text-sm text-[var(--accent-gold)]">
                      {t.savedTrips.newSuggestions}
                    </span>
                  </div>
                </div>
                {newPlaces.map((place) => (
                  <div key={place.id} className="animate-fade-in">
                    <NewPlacePOICard
                      place={place}
                      transport={selectedItinerary.transport}
                      isSelected={selectedNewPlaceIds.has(place.id)}
                      isSaving={savingPlaceIds.has(place.id)}
                      onToggleSelect={toggleNewPlaceSelection}
                    />
                  </div>
                ))}
              </>
            )}

            {newPlaces.length === 0 && (
              <button
                onClick={handleFindMore}
                disabled={loadingMore}
                aria-label={loadingMore ? 'Searching for more places' : 'Find more places for this trip'}
                className="w-full py-3 rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/10 hover:border-[var(--accent-teal)]/40 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t.results.searchingWiderArea}
                  </>
                ) : (
                  <>
                    <MapPin className="w-4 h-4" />
                    {t.results.findMorePlaces}
                  </>
                )}
              </button>
            )}
          </div>
        ) : groupedItineraries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg text-[var(--text-secondary)]">{t.savedTrips.noSavedTrips}</p>
            <p className="text-sm text-[var(--text-secondary)]/70 mt-2">{t.savedTrips.selectPlacesPrompt}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* H-213 v2: "Your plan" container — up to 2 pinned tiles. Survives
                mergeMode (pinned trips remain selectable like bucket items).
                When 2 pinned + not in mergeMode, surfaces a "Merge Plan" CTA
                that immediately merges the two pinned trips. */}
            {(onSetPinned || onUnpin) && (() => {
              const allTrips = groupedItineraries.flatMap(g => g.itineraries);
              const pinned = allTrips
                .filter(t => t.is_pinned)
                .sort((a, b) => {
                  const aT = a.pinned_at ? new Date(a.pinned_at).getTime() : 0;
                  const bT = b.pinned_at ? new Date(b.pinned_at).getTime() : 0;
                  return aT - bT;
                });
              if (pinned.length === 0) {
                if (mergeMode) return null;
                return (
                  <div
                    className="w-full rounded-2xl border-2 border-dashed border-[var(--border)] p-4 flex items-center gap-3"
                    role="note"
                    aria-label={t.pin?.emptyHintAria || 'How to pin a trip'}
                  >
                    <Pin className="w-4 h-4 text-[var(--text-secondary)]/60 flex-shrink-0" />
                    <span className="text-xs text-[var(--text-secondary)]/80 leading-relaxed">
                      {t.pin?.emptyHint || 'Pin a trip here — open any saved trip and tap the pin to mark it as your active plan.'}
                    </span>
                  </div>
                );
              }
              return (
                <div className="rounded-2xl border-2 border-[var(--accent-teal)]/60 bg-[var(--accent-teal)]/[0.04] p-2 space-y-2">
                  <div className="px-2 pt-1 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Pin className="w-3.5 h-3.5 text-[var(--accent-teal)]" style={{ fill: 'currentColor' }} />
                      <span className="text-xs font-semibold tracking-wide uppercase text-[var(--accent-teal)]">
                        {t.pin?.yourPlanLabel || 'Your plan'}
                      </span>
                    </div>
                    {pinned.length === 2 && !mergeMode && (
                      <button
                        onClick={handleMergePinnedTrips}
                        disabled={isMerging}
                        aria-label={t.pin?.mergePlanAria || 'Merge your two pinned trips into one plan'}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          isMerging
                            ? 'bg-[var(--accent-teal)] text-white opacity-70 cursor-not-allowed'
                            : 'bg-[var(--accent-teal)] text-white shadow-[0_0_16px_rgba(45,212,191,0.35)] hover:opacity-90 active:scale-95'
                        }`}
                      >
                        {isMerging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Merge className="w-3.5 h-3.5" />}
                        {t.pin?.mergePlan || 'Merge Plan'}
                      </button>
                    )}
                  </div>
                  {pinned.map(trip => (
                    <TripListItem
                      key={trip.id}
                      itinerary={trip}
                      onSelect={() => handleSelectItinerary(trip)}
                      onDelete={() => handleDelete(trip.id, trip.title)}
                      onUnpin={onUnpin ? () => handleUnpinTrip(trip) : undefined}
                      onRename={(newTitle) => onRenameTrip(trip.id, newTitle)}
                      onShare={onShareTrip ? async () => {
                        const url = await onShareTrip(trip.id);
                        if (!url) return;
                        await new Promise(r => setTimeout(r, 100));
                        if (navigator.share) {
                          try {
                            await navigator.share({
                              title: trip.title,
                              text: `${trip.title} — ${trip.places.length} places, optimised for ${trip.location}. Built with TripBoom:`,
                              url
                            });
                            return;
                          } catch { /* user cancelled or failed */ }
                        }
                        try { await navigator.clipboard.writeText(url); feedback.showCreditToast(t.toasts.linkCopied, 2500); } catch { /* clipboard unavailable */ }
                      } : undefined}
                      isDeleting={deletingId === trip.id}
                      mergeMode={mergeMode}
                      isMergeSelected={mergeSelectedIds.has(trip.id)}
                      onMergeToggle={() => toggleMergeSelection(trip.id)}
                    />
                  ))}
                </div>
              );
            })()}
            {/* H-213 v6 (Ian): Day/Location toggle moved out of the sticky header
                so the "Your Plan" tile sits ABOVE it. Pinning literally lifts a
                trip above the bucket filter. */}
            {groupedItineraries.length > 0 && (
              <div className="flex items-center justify-center">
                <div className="inline-flex items-center rounded-lg bg-[var(--bg-elevated)] p-0.5 border border-[var(--border)]">
                  <button
                    onClick={() => onGroupingChange('day')}
                    aria-pressed={groupingType === 'day'}
                    aria-label="Group trips by day"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      groupingType === 'day'
                        ? 'bg-[var(--accent-teal)]/20 text-[var(--accent-teal)]'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <Calendar className="w-3.5 h-3.5" />
                    {t.settings.groupByDay}
                    {!isPremium && <span className="opacity-60">({dayGroupCount}/{MAX_FREE_DAY_GROUPS})</span>}
                  </button>
                  <button
                    onClick={() => onGroupingChange('location')}
                    aria-pressed={groupingType === 'location'}
                    aria-label="Group trips by location"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      groupingType === 'location'
                        ? 'bg-[var(--accent-teal)]/20 text-[var(--accent-teal)]'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <MapPinned className="w-3.5 h-3.5" />
                    {t.settings.groupByLocation}
                    {!isPremium && <span className="opacity-60">({locationGroupCount}/{MAX_FREE_LOCATION_GROUPS})</span>}
                  </button>
                </div>
              </div>
            )}
            {/* Archive warning banner */}
            {!isPremium && (() => {
              const allTrips = groupedItineraries.flatMap(g => g.itineraries);
              const archivingSoon = allTrips.filter(i => {
                const days = daysUntilArchive(i.created_at, false);
                return days !== null && days > 0 && days <= 7;
              });
              if (archivingSoon.length === 0) return null;
              return (
                <div className="p-3 rounded-xl bg-[var(--accent-gold)]/10 border border-[var(--accent-gold)]/30 flex items-center gap-3">
                  <Crown className="w-5 h-5 text-[var(--accent-gold)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[var(--accent-gold)]">
                      {archivingSoon.length} {archivingSoon.length === 1 ? 'trip' : 'trips'} archiving soon
                    </p>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                      Free trips archive after 30 days, or when a location group exceeds the free limit. Upgrade to keep them active.
                    </p>
                  </div>
                  {onUpgrade && (
                    <button
                      onClick={onUpgrade}
                      aria-label="Upgrade to premium to keep trips active"
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[var(--accent-gold)] text-black flex-shrink-0"
                    >
                      Upgrade
                    </button>
                  )}
                </div>
              );
            })()}
            {sortedGroups.map((group, groupIndex) => {
              const freeLimit = groupingType === 'day' ? MAX_FREE_DAY_GROUPS : MAX_FREE_LOCATION_GROUPS;
              const groupArchived = isGroupArchived(group.itineraries, groupIndex, isPremium, freeLimit);
              return (
              <div key={group.groupKey}>
                <GroupHeader
                  label={group.groupLabel}
                  count={group.itineraries.length}
                  isExpanded={expandedGroups.has(group.groupKey)}
                  onToggle={() => toggleGroup(group.groupKey)}
                  onDelete={() => setConfirmDeleteGroup(group)}
                  isDeleting={deletingGroupKey === group.groupKey}
                  isArchived={groupArchived}
                />
                {expandedGroups.has(group.groupKey) && (
                  <div className="space-y-3 ml-7 mt-2">
                    {group.itineraries.filter(i => i.id !== pendingDeleteTrip?.id).map(itinerary => (
                      <TripListItem
                        key={itinerary.id}
                        itinerary={itinerary}
                        onSelect={() => handleSelectItinerary(itinerary)}
                        onDelete={() => handleDelete(itinerary.id, itinerary.title)}
                        onRename={(newTitle) => onRenameTrip(itinerary.id, newTitle)}
                        onShare={onShareTrip ? async () => {
                          const url = await onShareTrip(itinerary.id);
                          if (!url) return;
                          await new Promise(r => setTimeout(r, 100));
                          if (navigator.share) {
                            try {
                              await navigator.share({
                                title: itinerary.title,
                                text: `${itinerary.title} — ${itinerary.places.length} places, optimised for ${itinerary.location}. Built with TripBoom:`,
                                url
                              });
                              return;
                            } catch { /* user cancelled or failed */ }
                          }
                          try { await navigator.clipboard.writeText(url); feedback.showCreditToast(t.toasts.linkCopied, 2500); } catch { /* clipboard unavailable */ }
                        } : undefined}
                        isDeleting={deletingId === itinerary.id}
                        isArchived={isTripArchivedHybrid(itinerary.created_at, groupIndex, isPremium, freeLimit)}
                        onUpgrade={onUpgrade}
                        mergeMode={mergeMode}
                        isMergeSelected={mergeSelectedIds.has(itinerary.id)}
                        onMergeToggle={() => toggleMergeSelection(itinerary.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Undo toast — trip delete */}
      {pendingDeleteTrip && (
        <div className="fixed bottom-[4.5rem] left-4 right-4 z-30 max-w-2xl mx-auto">
          <div className="rounded-2xl shadow-2xl overflow-hidden border border-[var(--status-red)]/40 bg-[var(--bg-card)]">
            <div className="flex items-center gap-3 px-4 py-3">
              <Trash2 className="w-4 h-4 text-[var(--status-red)] flex-shrink-0" />
              <p className="flex-1 text-sm text-[var(--text-primary)] truncate min-w-0">
                <span className="text-[var(--text-secondary)]">Deleted: </span>
                <span className="font-medium">{pendingDeleteTrip.title}</span>
              </p>
              <button
                onClick={handleUndoDelete}
                aria-label={`Undo delete of ${pendingDeleteTrip.title}`}
                className="flex-shrink-0 px-5 py-2 rounded-xl font-bold text-sm tracking-wide bg-[var(--accent-gold)] text-[var(--bg-primary)] hover:opacity-90 active:scale-95 transition-all shadow-lg"
              >
                UNDO
              </button>
            </div>
            <div className="h-1.5 bg-[var(--bg-elevated)]">
              <div
                key={pendingDeleteTrip.id}
                className="h-full bg-[var(--status-red)] rounded-sm"
                style={{ animation: 'progress-shrink 5s linear forwards' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Group delete confirmation modal */}
      {confirmDeleteGroup && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmDeleteGroup(null)} role="presentation" />
          <div className="relative w-full max-w-sm rounded-2xl bg-[var(--bg-primary)] border border-[var(--border)] shadow-2xl p-6 animate-fade-in" role="dialog" aria-labelledby="delete-group-title">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[var(--status-red-bg)] flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-[var(--status-red)]" />
              </div>
              <h2 id="delete-group-title" className="text-base font-semibold text-[var(--text-primary)] leading-snug">
                Delete "{confirmDeleteGroup.groupLabel}"?
              </h2>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mb-5 leading-relaxed">
              This will permanently delete{' '}
              <span className="font-semibold text-[var(--text-primary)]">
                {confirmDeleteGroup.itineraries.length} trip{confirmDeleteGroup.itineraries.length !== 1 ? 's' : ''}
              </span>{' '}
              and all their saved places. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDeleteGroup(null)}
                aria-label="Cancel group deletion"
                className="flex-1 py-2.5 rounded-xl border border-[var(--border)] text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] transition-colors font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteGroup(confirmDeleteGroup)}
                disabled={deletingGroupKey === confirmDeleteGroup.groupKey}
                aria-label={`Delete all ${confirmDeleteGroup.itineraries.length} trips in ${confirmDeleteGroup.groupLabel}`}
                className="flex-1 py-2.5 rounded-xl bg-[var(--status-red)] text-white hover:opacity-90 transition-all font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingGroupKey === confirmDeleteGroup.groupKey ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</>
                ) : (
                  'Delete all'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unshare confirmation modal */}
      {confirmUnshare && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !isUnsharing && setConfirmUnshare(null)} role="presentation" />
          <div className="relative w-full max-w-sm rounded-2xl bg-[var(--bg-primary)] border border-[var(--border)] shadow-2xl p-6 animate-fade-in" role="dialog" aria-labelledby="unshare-title">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[var(--status-red-bg)] flex items-center justify-center flex-shrink-0">
                <Unlink2 className="w-5 h-5 text-[var(--status-red)]" />
              </div>
              <h2 id="unshare-title" className="text-base font-semibold text-[var(--text-primary)] leading-snug">
                Remove share link?
              </h2>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mb-5 leading-relaxed">
              Anyone with the link to{' '}
              <span className="font-semibold text-[var(--text-primary)]">
                "{confirmUnshare.title}"
              </span>{' '}
              will no longer be able to view it. You can always share it again later.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmUnshare(null)}
                disabled={isUnsharing}
                aria-label="Cancel unshare"
                className="flex-1 py-2.5 rounded-xl border border-[var(--border)] text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] transition-colors font-medium text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                aria-label={`Remove share link for ${confirmUnshare.title}`}
                onClick={async () => {
                  if (!onUnshareTrip) return;
                  setIsUnsharing(true);
                  const success = await onUnshareTrip(confirmUnshare.id);
                  if (success && selectedItinerary?.id === confirmUnshare.id) {
                    setSelectedItinerary({ ...selectedItinerary, is_shared: false });
                  }
                  setIsUnsharing(false);
                  setConfirmUnshare(null);
                }}
                disabled={isUnsharing}
                className="flex-1 py-2.5 rounded-xl bg-[var(--status-red)] text-white hover:opacity-90 transition-all font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUnsharing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Removing...</>
                ) : (
                  'Remove link'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {shareCopied && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border)] shadow-lg text-sm text-[var(--text-primary)]">
          {t.common.copiedToClipboard}
        </div>
      )}

      {/* H-214 Reevaluate panel — same component used for anon-viewer flow
          in SharedTripPage; here driven by the Sparkles button in the
          trip-detail top row. */}
      {reevaluatePanelOpen && selectedItinerary && (
        <ReevaluatePanel
          trip={selectedItinerary}
          language={language}
          t={t}
          onClose={() => setReevaluatePanelOpen(false)}
          onReevaluated={handleReevaluated}
          onLanguageChange={(lang: Language) => setLanguage(lang)}
          previousContext={reevaluateContext}
          showLanguagePicker={false}
        />
      )}

    </div>
  );
}
